import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { Box, CircularProgress, FormHelperText, Typography, useTheme } from "@mui/material";
import type { Klass, LexicalEditor, LexicalNode, LexicalNodeReplacement } from "lexical";
import { type FC, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useLexicalContext } from "./lexical-context";
import type { LexicalEditorProps } from "./lexical-editor";
import { EDITOR_THEME } from "./theme/theme";
import "./theme/theme.css";
import type { SerialisedEditorDesign } from "./types/serialised-editor-design";
import { $loadDesign } from "./utils/load-design";
import { EDITOR_NODES } from "./utils/nodes";

type LexicalEditorContainerProps = Pick<LexicalEditorProps, "format"> & {
  disabled?: boolean;
  error?: string;
  keyID: string;
  design?: SerialisedEditorDesign;
  nodes?: ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement>;
  onError: (e: Error, editor: LexicalEditor) => void;
  children: ReactElement;
};

export const LexicalEditorContainer: FC<LexicalEditorContainerProps> = ({
  disabled,
  format,
  design,
  error,
  keyID,
  nodes,
  onError,
  children,
}) => {
  const muiTheme = useTheme();
  const {
    focus: { focused },
    drag: { draggedOver },
    upload: { uploading },
  } = useLexicalContext();

  const nodesToUse = nodes ?? EDITOR_NODES[format];

  return (
    <>
      <Box
        className={`editor-container ${muiTheme.palette.mode}-mode ${format}-format`}
        sx={{
          position: "relative",
          border: 1,
          borderRadius: 1,
          borderStyle: "solid",
          borderColor: draggedOver ? "secondary.main" : "divider",
          background: (theme) =>
            focused
              ? `linear-gradient(${theme.palette.primary.main},${theme.palette.primary.main}) bottom/100% 5px no-repeat;`
              : "",
        }}
      >
        {uploading && <UploadingBox />}
        <LexicalComposer
          key={keyID}
          initialConfig={{
            editorState: $loadDesign(design),
            editable: !disabled,
            namespace: "ce",
            onError,
            nodes: nodesToUse,
            theme: EDITOR_THEME,
          }}
        >
          {children}
        </LexicalComposer>
        {error && (
          <FormHelperText error variant="filled">
            {error}
          </FormHelperText>
        )}
      </Box>
    </>
  );
};

const UploadingBox: FC = () => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        position: "absolute",
        top: "5%",
        left: "2%",
        width: "96%",
        height: "92%",
        zIndex: 100,
        backgroundColor: "white",
        opacity: "0.8",
        borderRadius: "5px",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "text.disabled",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        justifyContent: "center",
        alignItems: "center",
        color: "text.secondary",
      }}
    >
      <CircularProgress />
      <Typography>{t("Inserting...")}</Typography>
    </Box>
  );
};
